import type { CompareOption } from "./side-by-side"
import type { ReviewVersion } from "../api/use-review-versions"

/** The master is always offered, and always first. */
export const MASTER_COMPARE_ID = "master"

interface MasterInput {
  url: string
  name: string
  regNo: string
  pageCount: number
}

/** The printed label's own filename, for naming an earlier submission. */
interface FplInput {
  name: string
}

/**
 * What the right pane may be compared against, in the order offered: the
 * master first, then earlier versions as the caller ordered them. The review
 * being read is excluded — a label compared against itself shows nothing.
 */
export function buildCompareOptions(
  master: MasterInput,
  fpl: FplInput,
  versions: readonly ReviewVersion[],
  currentReviewId: string,
): [CompareOption, ...CompareOption[]] {
  const masterOption: CompareOption = {
    id: MASTER_COMPARE_ID,
    label: "Governing master",
    paneLabel: master.name,
    paneSubtitle: "Approved master",
    paneProvenance: `Reg. ${master.regNo}`,
    documentUrl: master.url,
    pageCount: master.pageCount,
  }

  const versionOptions = versions
    .filter((v) => v.reviewId !== currentReviewId)
    .map<CompareOption>((v) => ({
      id: v.reviewId,
      label: `Previous version (${v.label})`,
      // The label's own filename: an earlier submission is a version of the
      // label, and calling it `master.pdf` would say it is the approved one.
      paneLabel: fpl.name,
      paneSubtitle: "Earlier submission",
      paneProvenance: `Reg. ${master.regNo} · ${v.label}`,
      documentUrl: v.fplUrl,
      // Placeholder until the pane reports the real count from pdf.js.
      pageCount: 1,
    }))

  return [masterOption, ...versionOptions]
}
